import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import TextComboInput from "@shared/v2/TextComboInput";
import { CheckSolidV6 } from "@shared/v2/Icomoon";
import { produce } from "immer";
// eslint-disable-next-line import/no-unresolved
import socialMediaNormalizer, { SOCIAL_MEDIA_CONFIG } from "@shared/socialMediaNormalizer";
import { updateSocialMediaPromise } from "../actions/updateSocialMedia";
import { closeEditSocialMediaModal, updatePersonData } from "../actions/personDetailActionCreators";

const SocialMediaIcon = ({ icon, isValid }) => (
  <span
    className={`tw-flex tw-justify-center tw-items-center tw-w-[38px] tw-h-[38px] tw-rounded-full ${isValid ? "tw-bg-[#DEF3F3] tw-text-[#59C4C4]" : "tw-bg-gray-15 tw-text-gray-50"
      }`}
  >
    <i className={icon} />
  </span>
);

SocialMediaIcon.propTypes = {
  icon: PropTypes.string.isRequired,
  isValid: PropTypes.bool.isRequired,
};

const EditSocialMediaForm = () => {
  const dispatch = useDispatch();
  const person = useSelector((state) => state.personDetail.person);
  const [editablePerson, setEditablePerson] = useState(person);
  const [errors, setErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationStates, setValidationStates] = useState({});

  useEffect(() => {
    setEditablePerson(person);
  }, [person]);

  const handleEditableValueChange = (value, key) => {
    setEditablePerson(
      produce(editablePerson, (draft) => {
        // eslint-disable-next-line no-param-reassign
        draft.data.attributes[key].value = value;
      }),
    );

    setValidationStates((prev) => ({
      ...prev,
      [key]: {
        isValid: Boolean(value),
        isEmpty: !value,
        error: null,
      },
    }));
  };

  const handleBlur = (value, key, config) => {
    const result = socialMediaNormalizer({
      input: value,
      domain: config.domain,
      exceptions: config.exceptions || [],
      errorMessages: config.errorMessages,
    });

    if ("success" in result && result.success !== null) {
      setEditablePerson(
        produce(editablePerson, (draft) => {
          // eslint-disable-next-line no-param-reassign
          draft.data.attributes[key].value = result.success;
        }),
      );
    }

    setValidationStates((prev) => ({
      ...prev,
      [key]: {
        isValid: "success" in result && result.success !== null,
        isEmpty: "success" in result && result.success === null,
        error: "error" in result ? result.error : null,
      },
    }));
  };

  const hasErrors = useMemo(
    () => Object.values(validationStates).some((state) => state.error !== null),
    [validationStates],
  );

  const saveChanges = () => {
    if (isSaving) return;
    setIsSaving(true);

    const socialMediaAttributes = SOCIAL_MEDIA_CONFIG.reduce((acc, config) => {
      const { key } = config;
      const { id, value } = editablePerson.data.attributes[key];
      const result = socialMediaNormalizer({
        input: value || "",
        domain: config.domain,
        exceptions: config.exceptions || [],
        errorMessages: config.errorMessages,
      });
      const normalizedValue = "success" in result ? result.success : value;
      acc[key] = { id, value: normalizedValue, name: key };
      return acc;
    }, {});

    const personId = editablePerson.data.id;

    updateSocialMediaPromise(personId, socialMediaAttributes)
      .then((r) => {
        dispatch(updatePersonData(r.data));
        dispatch(closeEditSocialMediaModal());
      })
      .catch((err) => {
        setErrors(err.response.data.errors || ["Something went wrong..."]);
        setIsSaving(false);
      });
  };

  const getValidationState = (key) => {
    const state = validationStates[key];
    if (!state) {
      const currentValue = editablePerson.data.attributes[key].value;
      return {
        isValid: Boolean(currentValue),
        isEmpty: !currentValue,
        error: null,
      };
    }
    return state;
  };

  return (
    <>
      <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-gap-[16px] tw-overflow-auto">
        {errors && (
          <Alert variant="error">
            {errors.map((error, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={index}>
                <strong>Error: </strong> {error}
                <br />
              </span>
            ))}
          </Alert>
        )}

        {SOCIAL_MEDIA_CONFIG.map((config) => {
          const { key, label, icon } = config;
          const { value } = editablePerson.data.attributes[key];
          const { isValid, error } = getValidationState(key);
          const showValidState = isValid && !error;

          return (
            <div key={key} className="tw-flex tw-items-start tw-gap-[12px]">
              <TextComboInput
                containerClassName="tw-flex-1"
                className={showValidState ? "!tw-border-gray-30" : ""}
                label={label}
                value={value}
                placeholder="Enter username or link"
                onChange={(e) => handleEditableValueChange(e.target.value, key)}
                onBlur={(e) => handleBlur(e.target.value, key, config)}
                error={error}
                trailing={showValidState && <CheckSolidV6 size="m" className="tw-text-[#46C771]" />}
              />
              <div className="tw-mt-[24px]">
                <SocialMediaIcon icon={icon} isValid={showValidState} />
              </div>
            </div>
          );
        })}
      </Modal.Body>

      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        <Button size="medium" schema="tertiary" onClick={() => dispatch(closeEditSocialMediaModal())}>
          Cancel
        </Button>
        <Button size="medium" onClick={saveChanges} disabled={hasErrors || isSaving} isLoading={isSaving}>
          Save Changes
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditSocialMediaForm;
